import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Stack,
    Text,
    useStyleConfig,
    Button,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {FormattedMessage, useIntl} from 'react-intl'
import ProductTile from '../../../product-tile'
import {useGlobaleContext} from '../../../../contexts/globale'
import {SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../../../../constants'
import {yieldToMain} from '../../../../utils/common-utils'
import LoadingSpinner from '../../../../components/loading-spinner'

export const EspProducts = ({...rest}) => {
    const {
        headlineAlignmentMobile = 'left',
        headlineAlignmentDesktop = 'center',
        headlineText,
        headlineTextColor,
        headlineTextFontFamily,
        headlineTag,
        subHeadlineText,
        subHeadlineTextColor,
        subHeadlineTextFontFamily,
        subHeadlineTag,
        backgroundColor,
        displayCount,
        enableOverflow,
        buttonBgColor,
        buttonTextColor,
        buttonTextFontFamily
    } = rest
    const intl = useIntl()
    const [showSpinner, setShowSpinner] = useState(false)
    const [displayAll, setDisplayAll] = useState(false)
    const [isTruncated, setIsTruncated] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const [rendered, setRendered] = useState(true)
    const textRef = useRef(null)
    const productListStyles = useStyleConfig('ProductListStyles')
    const infiniteScrollStyles = useStyleConfig('InfiniteScrollStyles')
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const {countryISO, currencyCode} = useGlobaleContext()
    const productIds = []
    const customLayoutIndex = [3, 6, 9]
    const maxProduct = 12
    let index = 1

    while (`product${index}` in rest) {
        productIds.push(rest[`product${index}`])
        delete rest[`product${index}`]
        index++
    }

    useEffect(() => {
        setRendered(false)
        setIsExpanded(false)
    }, [subHeadlineText])

    useEffect(() => {
        if (textRef.current && !rendered) {
            const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight)
            const maxHeight = lineHeight * 3
            setIsTruncated(textRef.current.scrollHeight > maxHeight)
            setRendered(true)
        }
    }, [subHeadlineText, rendered])

    // aria-label based on state
    const messages = {
        view_more: {id: 'view_more', defaultMessage: 'View More'},
        view_less: {id: 'view_less', defaultMessage: 'View Less'}
    }

    const ariaLabel = intl.formatMessage(displayAll ? messages.view_less : messages.view_more)

    const {data: products, isLoading} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: selectedAttributes,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: Boolean(productIds.length)
        }
    )

    const handleClick = async () => {
        setShowSpinner(true)
        await yieldToMain()
        setDisplayAll(!displayAll)
        setTimeout(() => {
            setShowSpinner(false)
        }, 100)
    }

    const productDisplay = (index) => {
        return {
            base: displayCount < index + 1 && !displayAll && !enableOverflow ? 'none' : 'block'
        }
    }

    const productList = products?.data?.slice(0, 12) || []

    const isSpecialLayout =
        (customLayoutIndex.includes(productList.length) && displayAll) ||
        (displayCount % 4 === 1 && !displayAll && displayCount < maxProduct) ||
        (displayCount % 3 === 0 && !displayAll && displayCount < maxProduct)

    const smallDesktopTileSize = isSpecialLayout ? '25%' : '24.5%'
    const desktopTileSize = isSpecialLayout ? '25%' : '24.6%'

    return (
        <Stack gap="0" paddingTop="10px" paddingBottom={{md: '20px'}} paddingX="0">
            <Box width="100%" background={backgroundColor}>
                {(headlineText || subHeadlineText) && (
                    <Box
                        textAlign={{base: headlineAlignmentMobile, lg: headlineAlignmentDesktop}}
                        paddingTop={{base: '15px', lg: '30px'}}
                        paddingBottom={{base: '15px', lg: '30px'}}
                        paddingX={{
                            base: '15px',
                            lg: headlineAlignmentDesktop === 'center' ? '15px' : '60px',
                            'xl-large': headlineAlignmentDesktop === 'center' ? '15px' : '180px'
                        }}
                        marginX="auto"
                        maxWidth={{
                            base: '100%',
                            lg: headlineAlignmentDesktop === 'center' ? '700px' : '100%'
                        }}
                    >
                        {headlineText && (
                            <Text
                                as={headlineTag}
                                color={headlineTextColor}
                                fontFamily={headlineTextFontFamily}
                                fontWeight="400"
                                fontSize="25px"
                                lineHeight="normal"
                                letterSpacing="0.125px"
                                marginBottom="20px"
                            >
                                {headlineText}
                            </Text>
                        )}
                        {subHeadlineText && (
                            <Text
                                as={subHeadlineTag}
                                ref={textRef}
                                noOfLines={isExpanded ? undefined : 3}
                                maxHeight="auto"
                                color={subHeadlineTextColor}
                                fontFamily={subHeadlineTextFontFamily}
                                fontWeight="300"
                                fontSize="16px"
                                lineHeight="25px"
                                letterSpacing="0.08px"
                                sx={{
                                    p: {
                                        color: subHeadlineTextColor,
                                        margin: '0px 0px 5px 0px',
                                        lineHeight: '25px',
                                        fontWeight: '300',
                                        fontFamily: 'body',
                                        fontSize: '16px'
                                    },
                                    a: {
                                        color: subHeadlineTextColor,
                                        textDecoration: 'underline',
                                        lineHeight: '25px',
                                        fontSize: '16px',
                                        fontWeight: '300',
                                        fontFamily: 'body',
                                        _hover: {
                                            textDecoration: 'none',
                                            color: subHeadlineTextColor
                                        }
                                    },
                                    span: {
                                        color: subHeadlineTextColor,
                                        lineHeight: '25px',
                                        fontSize: '16px',
                                        fontWeight: '300',
                                        fontFamily: 'body'
                                    }
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: subHeadlineText
                                }}
                            />
                        )}

                        {isTruncated && (
                            <Button
                                color={subHeadlineTextColor}
                                _active={{color: subHeadlineTextColor}}
                                textDecoration="underline"
                                textUnderlineOffset="4px"
                                fontSize="16px"
                                marginTop="5px"
                                variant="link"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded
                                    ? intl.formatMessage({
                                          id: 'read.less',
                                          defaultMessage: 'Read Less'
                                      })
                                    : intl.formatMessage({
                                          id: 'read.more',
                                          defaultMessage: 'Read More'
                                      })}
                            </Button>
                        )}
                    </Box>
                )}

                <Flex
                    wrap="wrap"
                    justify="center"
                    columnGap="6px"
                    rowGap={{base: '15px', lg: '70px'}}
                >
                    {productList.map((product, index) => (
                        <Box
                            key={product.id}
                            display={productDisplay(index)}
                            maxWidth={{
                                base: '49%',
                                lg: smallDesktopTileSize,
                                '2xl': desktopTileSize
                            }}
                        >
                            <ProductTile
                                key={`${product.productId || product.id}-${index}`}
                                product={product}
                                index={index}
                                showName={true}
                                showPrice={true}
                                showQuickAdd={false}
                                hideVariations={true}
                                showImageSlider={false}
                                showBadge={false}
                                showPromotions={false}
                                preventTileClick={false}
                                isplp={false}
                                dynamicImageProps={{
                                    imageProps: {
                                        loading: index < 4 ? 'eager' : 'lazy',
                                        fetchpriority: index < 4 ? 'high' : 'auto'
                                    },
                                    widths: ['225w', '325w', '384w', '576w', '650w', '768w', '960w']
                                }}
                            />
                        </Box>
                    ))}
                </Flex>

                {/* loader */}
                {(showSpinner || isLoading) && <LoadingSpinner position="fixed" top="auto" />}
                {/* view more button */}
                {!enableOverflow && (
                    <Box
                        className="view-more-ctnr"
                        {...infiniteScrollStyles.wrapper}
                        display={productList.length > displayCount ? 'block' : 'none'}
                        paddingTop={{base: '15px', lg: '70px'}}
                    >
                        <Flex {...productListStyles.footer}>
                            <Button
                                aria-label={ariaLabel}
                                {...infiniteScrollStyles.button}
                                onClick={handleClick}
                                backgroundColor={buttonBgColor}
                                color={buttonTextColor}
                                fontFamily={buttonTextFontFamily}
                                sx={{
                                    _hover: {
                                        backgroundColor: `color-mix(in srgb, ${buttonBgColor} 88%, white 12%)`
                                    }
                                }}
                                border="none"
                            >
                                {displayAll && (
                                    <FormattedMessage id="view_less" defaultMessage="View Less" />
                                )}
                                {!displayAll && (
                                    <FormattedMessage id="view_more" defaultMessage="View More" />
                                )}
                            </Button>
                        </Flex>
                    </Box>
                )}
            </Box>
        </Stack>
    )
}

EspProducts.propTypes = {
    headlineAlignmentMobile: PropTypes.string,
    headlineAlignmentDesktop: PropTypes.string,
    headlineText: PropTypes.string,
    headlineTextColor: PropTypes.string,
    headlineTextFontFamily: PropTypes.string,
    headlineTag: PropTypes.string,
    subHeadlineText: PropTypes.string,
    subHeadlineTextColor: PropTypes.string,
    subHeadlineTextFontFamily: PropTypes.string,
    subHeadlineTag: PropTypes.string,
    backgroundColor: PropTypes.string,
    displayCount: PropTypes.string,
    enableOverflow: PropTypes.bool,
    buttonBgColor: PropTypes.string,
    buttonTextColor: PropTypes.string,
    buttonTextFontFamily: PropTypes.string
}

export default EspProducts
